import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import {
  ChevronLeft,
  Heart,
  MessageCircle,
  UserPlus,
  Users,
  Bell,
  Share2,
  Star,
  Check,
  Trash2,
  Settings,
  Loader2,
  User,
} from 'lucide-react';
import {
  useNotifications as useNotificationsQuery,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification as useDeleteNotificationMutation,
} from '@/hooks/useNotifications';
import { timeAgo } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { Notification as SdkNotification } from '@/types';

/** Extract display fields from an SDK notification. */
function parseNotification(n: SdkNotification) {
  // Cast to access API response fields that aren't in the SDK type
  const raw = n as Record<string, unknown>;
  const data = (n.data ?? {}) as Record<string, unknown>;
  const user = data.user as Record<string, unknown> | undefined;
  const post = data.post as Record<string, unknown> | undefined;

  // API returns fromUserName/fromUserUsername at root level, not nested in data.user
  const fromUserName = raw.fromUserName as string | undefined;
  const fromUserUsername = raw.fromUserUsername as string | undefined;
  const notificationType = n.type || (raw.notificationType as string) || 'system';

  // Avatar: API doesn't include fromUserAvatar, so check data.actorAvatar fallback
  const actorAvatar = (data.actorAvatar as string) || (user?.avatarUrl as string) || (user?.avatar as string);

  // For navigation: postId (for post-related notifications)
  const postId = (raw.postId as string) || (raw.rootId as string) || (data.postId as string) || undefined;

  return {
    // API returns notificationId, SDK type has id
    id: (raw.notificationId as string) || n.id,
    type: notificationType,
    title: fromUserName || fromUserUsername || (user?.displayName as string) || (user?.name as string) || (user?.username as string) || (data.title as string) || 'Pulse',
    message: (raw.message as string) || (data.message as string) || (data.body as string) || getDefaultMessage(notificationType),
    time: n.createdAt ? timeAgo(n.createdAt) : '',
    avatar: processAvatarUrl(actorAvatar) || undefined,
    image: (post?.imageUrl as string) || (data.imageUrl as string) || undefined,
    read: n.readAt != null || (raw.read as boolean) === true,
    // Navigation targets
    postId,
    username: fromUserUsername,
  };
}

function getDefaultMessage(type: string): string {
  switch (type) {
    case 'like': return 'liked your post';
    case 'comment': return 'commented on your post';
    case 'follow': return 'started following you';
    case 'group_invite': return 'invited you to join';
    case 'mention': return 'mentioned you';
    case 'share': return 'shared your post';
    default: return '';
  }
}

interface NotificationsProps {
  onBack?: () => void;
  onUnreadCountChange?: (count: number) => void;
}

export default function Notifications({ onBack, onUnreadCountChange }: NotificationsProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('unread');
  const [selectedNotification, setSelectedNotification] = useState<ReturnType<typeof parseNotification> | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Data hooks
  const {
    data: notificationsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsQuery({ unreadOnly: activeTab === 'unread' });

  const { data: unreadCount = 0 } = useUnreadCount();

  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();
  const deleteMutation = useDeleteNotificationMutation();

  // Flatten paginated notifications
  const rawNotifications = notificationsData?.pages.flatMap(
    (page) => (page.data as SdkNotification[]) ?? []
  ) ?? [];

  const notifications = rawNotifications.map(parseNotification);

  // Sync unread count to parent
  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  // Entry animations
  useEffect(() => {
    gsap.fromTo(
      headerRef.current,
      { y: -20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' }
    );

    if (listRef.current) {
      gsap.fromTo(
        listRef.current.children,
        { x: -20, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, stagger: 0.05, ease: 'power2.out', delay: 0.2 }
      );
    }
  }, [activeTab]);

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const markAsRead = (id: string) => {
    markReadMutation.mutate(id);
  };

  const markAllAsRead = () => {
    // Pass the first (most recent) notification ID to mark all up to that point
    const firstNotificationId = notifications[0]?.id;
    if (firstNotificationId) {
      markAllReadMutation.mutate(firstNotificationId);
    }
  };

  const deleteNotification = (id: string) => {
    deleteMutation.mutate(id);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like':
        return <Heart size={18} className="text-[#FF4D4D]" fill="#FF4D4D" />;
      case 'comment':
        return <MessageCircle size={18} className="text-[#A38BFF]" fill="#A38BFF" />;
      case 'follow':
        return <UserPlus size={18} className="text-[#1DB954]" />;
      case 'group_invite':
        return <Users size={18} className="text-[#25F4EE]" />;
      case 'mention':
        return <Bell size={18} className="text-[#F5F7FF]" />;
      case 'system':
        return <Star size={18} className="text-[#FFD700]" fill="#FFD700" />;
      case 'share':
        return <Share2 size={18} className="text-[#A38BFF]" />;
      default:
        return <Bell size={18} className="text-[#A7B0C8]" />;
    }
  };

  const handleNotificationClick = (notification: ReturnType<typeof parseNotification>) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    setSelectedNotification(notification);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedNotification(null);
    document.body.style.overflow = '';
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 pb-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]"
              >
                <ChevronLeft size={22} />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>
                Notifications
              </h1>
              {unreadCount > 0 && (
                <p className="text-xs text-[#A38BFF]">{unreadCount} unread</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={markAllReadMutation.isPending}
                className="px-3 py-1.5 bg-[#141821] rounded-full text-xs text-[#A7B0C8] flex items-center gap-1"
              >
                {markAllReadMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                <span>Mark all read</span>
              </button>
            )}
            <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
              <Settings size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setActiveTab('unread')}
            className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors relative ${
              activeTab === 'unread'
                ? 'bg-[#A38BFF] text-[#0B0D10]'
                : 'bg-[#141821] text-[#A7B0C8]'
            }`}
          >
            Unread
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF4D4D] rounded-full text-[10px] flex items-center justify-center text-white font-bold">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-[#A38BFF] text-[#0B0D10]'
                : 'bg-[#141821] text-[#A7B0C8]'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div ref={listRef} className="px-4 py-4">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={32} className="animate-spin text-[#A38BFF]" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell size={64} className="text-[#2A2F3A] mx-auto mb-4" />
            <p className="text-[#A7B0C8] text-lg">No notifications</p>
            <p className="text-sm text-[#A7B0C8] mt-1">
              {activeTab === 'unread' ? "You're all caught up!" : 'Check back later'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`relative bg-[#141821] rounded-xl p-4 flex items-start gap-3 cursor-pointer active:scale-[0.99] transition-all ${
                  !notification.read ? 'border border-[#A38BFF]/30' : ''
                }`}
              >
                {!notification.read && (
                  <span className="absolute top-4 right-4 w-2 h-2 bg-[#A38BFF] rounded-full" />
                )}

                {/* Avatar or Icon */}
                <div className="relative flex-shrink-0">
                  {notification.avatar ? (
                    <div className="relative">
                      <img
                        src={notification.avatar}
                        alt={notification.title}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#141821] rounded-full flex items-center justify-center">
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#0B0D10] flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm text-[#F5F7FF]">
                    <span className="font-semibold">{notification.title}</span>{' '}
                    <span className="text-[#A7B0C8]">{notification.message}</span>
                  </p>
                  <p className="text-xs text-[#A7B0C8] mt-1">{notification.time}</p>
                </div>

                {/* Preview Image */}
                {notification.image && (
                  <img
                    src={notification.image}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  />
                )}

                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }}
                  className="absolute bottom-2 right-2 p-2 text-[#A7B0C8] opacity-0 group-hover:opacity-100 hover:text-[#FF4D4D] transition-opacity"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />

        {isFetchingNextPage && (
          <div className="flex justify-center py-6">
            <Loader2 size={24} className="animate-spin text-[#A38BFF]" />
          </div>
        )}

        {/* Clear All */}
        {notifications.length > 0 && (
          <button
            onClick={markAllAsRead}
            className="w-full mt-6 py-3 text-sm text-[#A7B0C8] hover:text-[#FF4D4D] transition-colors"
          >
            Clear all notifications
          </button>
        )}
      </div>

      {/* Modal rendered as portal to body to avoid scroll issues */}
      {selectedNotification && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/70"
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
          }}
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg bg-[#141821] rounded-t-[24px] p-6"
            style={{
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '70vh',
              overflowY: 'auto',
              animation: 'slideUpModal 0.25s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
            </div>

            <div className="flex items-center gap-4 mb-4">
              {selectedNotification.avatar ? (
                <img
                  src={selectedNotification.avatar}
                  alt={selectedNotification.title}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#0B0D10] flex items-center justify-center">
                  {getNotificationIcon(selectedNotification.type)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-[#F5F7FF] truncate">
                  {selectedNotification.title}
                </h3>
                <p className="text-sm text-[#A7B0C8]">{selectedNotification.message}</p>
                <p className="text-xs text-[#A7B0C8] mt-1">{selectedNotification.time}</p>
              </div>
            </div>

            {selectedNotification.image && (
              <img
                src={selectedNotification.image}
                alt=""
                className="w-full aspect-video object-cover rounded-xl mb-4"
              />
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  // Mark as read if not already
                  if (!selectedNotification.read) {
                    markAsRead(selectedNotification.id);
                  }
                  closeModal();
                  // Navigate to the appropriate destination
                  if (selectedNotification.postId) {
                    navigate(`/post/${selectedNotification.postId}`);
                  } else if (selectedNotification.username && selectedNotification.type === 'follow') {
                    navigate(`/profile/${selectedNotification.username}`);
                  }
                }}
                className="flex-1 py-3 bg-[#A38BFF] rounded-xl text-sm font-medium text-[#0B0D10]"
              >
                View
              </button>
              <button
                onClick={() => {
                  if (!selectedNotification.read) {
                    markAsRead(selectedNotification.id);
                  }
                  closeModal();
                }}
                className="flex-1 py-3 bg-[#0B0D10] rounded-xl text-sm font-medium text-[#F5F7FF] border border-white/[0.08]"
              >
                Mark as Read
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
